/**
 * SORGU ANAHTARI FABRIKASI — platform yonetimi.
 *
 * NEDEN FABRIKA: bir mutasyonun yanlis anahtari (ya da hic anahtar) gecersiz
 * kilmasi, "yaptim ama listede gorunmuyor" sinifindan hatalarin tek sebebi.
 * Elle yazildiginda `['admin','users']` ile `['admin','user']` arasindaki fark
 * derleme zamaninda gorunmez; buradan uretildiginde gorunur.
 *
 * ============================ HIYERARSI ====================================
 *   ['admin']
 *   ['admin','users']                       -> tum kullanici sorgulari
 *   ['admin','users','list', filtreAnahtari]   sonsuz liste
 *   ['admin','users','detail', userId]         tek kullanici
 *   ['admin','organizations']               -> tum isletme sorgulari
 *   ['admin','organizations','list', filtreAnahtari]
 *   ['admin','organizations','detail', organizationId]
 *   ['admin','audit', filtreAnahtari]
 *   ['admin','metrics','users', period, from, to]
 *   ['admin','metrics','overview']
 *   ['admin','metrics','timeseries', metric, period]
 *   ['admin','activity']
 *   ['admin','broadcasts']
 *   ['admin','warnedTasks', sort]

 *
 * Onek eslemesi TanStack'in varsayilanidir: `['admin','users']` ile yapilan
 * gecersiz kilma altindaki HER seyi kapsar.
 *
 * ======================= GECERSIZ KILMA HARITASI ===========================
 * (mutasyon -> tazelenmesi gereken sorgular; uygulamasi invalidation.ts'te)
 *
 *   setUserStatus       -> users(), organizationsRoot(), auditRoot(),
 *                          metricsUsersRoot()
 *       users:          satirdaki durum rozeti ve detay sayfasi
 *       organizations:  isletme detayindaki uye satiri ayni kullanici
 *       audit:          sunucu her islem icin denetim kaydi yaziyor
 *       metrics:        totals.active / totals.inactive aninda degisiyor
 *
 *   setUserRole         -> users(), auditRoot()
 *   resetUserPassword   -> users(), auditRoot()
 *       (mustChangePassword bayragi degisiyor; sayilar degismiyor)
 *
 *   setOrganizationStatus -> organizations(), users(), auditRoot(),
 *                            metricsUsersRoot()
 *       users:          uyelerin jetonlari iptal ediliyor, liste isletme adi
 *                       ve durumunu gosteriyor
 *       metrics:        totals.activeOrganizations degisiyor
 *
 *   sendBroadcast       -> broadcasts()
 *
 *   resolveWarnedTask   -> warnedTasksRoot(), metricsRoot(), activity()
 *       kuyruk:         kapanan satir listeden duser
 *       metrics:        pazaryeri ozeti durum sayilarini tasiyor
 *
 * Kaba davraniliyor: bir uc ekleyenin "hangi listeleri bozdum" sorusunu
 * dusunmek zorunda kalmamasi, fazladan bir istekten daha degerli.
 * ========================================================================== */

export const adminKeys = {
  all: ['admin'] as const,

  /* --- Kullanicilar ------------------------------------------------------- */
  usersRoot: () => [...adminKeys.all, 'users'] as const,
  userLists: () => [...adminKeys.usersRoot(), 'list'] as const,
  /** Filtre anahtarin PARCASI: farkli filtre = farkli onbellek girisi. */
  userList: (filtersKey: string) => [...adminKeys.userLists(), filtersKey] as const,
  userDetail: (userId: string) => [...adminKeys.usersRoot(), 'detail', userId] as const,

  /* --- Isletmeler --------------------------------------------------------- */
  organizationsRoot: () => [...adminKeys.all, 'organizations'] as const,
  organizationLists: () => [...adminKeys.organizationsRoot(), 'list'] as const,
  organizationList: (filtersKey: string) =>
    [...adminKeys.organizationLists(), filtersKey] as const,
  organizationDetail: (organizationId: string) =>
    [...adminKeys.organizationsRoot(), 'detail', organizationId] as const,
  /**
   * Isletmenin banka hesaplari (IBAN platform yonetiminde, 2026-08-30).
   * `organizationsRoot()` ALTINDA: isletme pasiflestirildiginde hesap listesi de
   * tazelensin — ayri bir kok olsaydi o gecersiz kilma bu sorguyu atlardi.
   */
  organizationAccounts: (organizationId: string) =>
    [...adminKeys.organizationsRoot(), 'accounts', organizationId] as const,

  /* --- Denetim kaydi ------------------------------------------------------ */
  auditRoot: () => [...adminKeys.all, 'audit'] as const,
  audit: (filtersKey: string) => [...adminKeys.auditRoot(), filtersKey] as const,

  /* --- Metrikler ---------------------------------------------------------- */
  metricsRoot: () => [...adminKeys.all, 'metrics'] as const,
  metricsUsersRoot: () => [...adminKeys.metricsRoot(), 'users'] as const,
  metricsUsers: (period: string, from: string | null, to: string | null) =>
    [...adminKeys.metricsUsersRoot(), period, from ?? '-', to ?? '-'] as const,
  metricsOverview: () => [...adminKeys.metricsRoot(), 'overview'] as const,
  metricsTimeseries: (metric: string, period: string) =>
    [...adminKeys.metricsRoot(), 'timeseries', metric, period] as const,

  /* --- Uyari kuyrugu (2026-09-23) ------------------------------------------ */
  warnedTasksRoot: () => [...adminKeys.all, 'warnedTasks'] as const,
  warnedTasks: (sort: string) => [...adminKeys.warnedTasksRoot(), sort] as const,

  /* --- Akis ve duyurular --------------------------------------------------- */
  activity: () => [...adminKeys.all, 'activity'] as const,
  broadcasts: () => [...adminKeys.all, 'broadcasts'] as const,
};

/**
 * Filtre nesnesini KARARLI bir anahtara cevirir.
 *
 * `JSON.stringify(filters)` YETMEZ: alan sirasi degistiginde ayni filtre iki
 * ayri onbellek girisi uretir ve liste sebepsiz yere yeniden yuklenir. Alanlar
 * bu yuzden ada gore siralaniyor.
 */
export const filtersKey = (filters: Record<string, string | boolean | null>): string =>
  Object.keys(filters)
    .sort()
    .map((key) => `${key}=${String(filters[key] ?? '')}`)
    .join('&');
